import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  FaArrowRight,
  FaCalendarAlt,
  FaCheckCircle,
  FaEnvelope,
  FaPhoneAlt,
  FaUserPlus,
} from "react-icons/fa";

import Reveal from "../../../components/Reveal/Reveal";
import { getSettings } from "../../../services/settingsServices";

const DEFAULT_SETTINGS = {
  nom_club: "le club",
  mail_club: "",
  email_club: "",
  tel_club: "",
  telephone_club: "",
};

const normalizePhoneHref = (phone = "") => {
  const clean = String(phone).replace(/\s+/g, "");
  return clean ? `tel:${clean}` : "";
};

export default function TrialCtaBanner() {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);

  useEffect(() => {
    let mounted = true;

    const fetchSettings = async () => {
      try {
        const response = await getSettings();
        const clubSettings = Array.isArray(response) ? response[0] : response;

        if (!mounted || !clubSettings) return;

        setSettings({
          ...DEFAULT_SETTINGS,
          ...clubSettings,
        });
      } catch (error) {
        console.error("Erreur lors du chargement du bandeau CTA :", error);
      }
    };

    fetchSettings();

    return () => {
      mounted = false;
    };
  }, []);

  const clubName = settings?.nom_club || "le club";

  const email = useMemo(() => {
    return settings?.mail_club || settings?.email_club || settings?.email || "";
  }, [settings]);

  const phone = useMemo(() => {
    return (
      settings?.tel_club ||
      settings?.telephone_club ||
      settings?.phone ||
      ""
    );
  }, [settings]);

  const phoneHref = useMemo(() => normalizePhoneHref(phone), [phone]);

  return (
    <section className="relative overflow-hidden bg-white py-16 text-slate-950 dark:bg-slate-950 dark:text-white">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(242,113,49,0.14),transparent_34%),radial-gradient(circle_at_bottom_right,rgba(59,130,246,0.10),transparent_32%)]" />
      <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(148,163,184,0.13)_1px,transparent_1px),linear-gradient(to_bottom,rgba(148,163,184,0.13)_1px,transparent_1px)] bg-[size:42px_42px] opacity-35 dark:opacity-15" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal variant="zoom">
          <div className="relative overflow-hidden rounded-[2.5rem] bg-slate-950 p-6 text-white shadow-2xl sm:p-8 lg:p-10">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(242,113,49,0.35),transparent_36%),radial-gradient(circle_at_bottom_right,rgba(59,130,246,0.24),transparent_34%)]" />
            <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.08)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.08)_1px,transparent_1px)] bg-[size:42px_42px] opacity-20" />

            <div className="relative grid gap-8 lg:grid-cols-[1.15fr_0.85fr] lg:items-center">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-2 text-xs font-bold uppercase tracking-[0.22em] text-white/70 backdrop-blur-xl">
                  <FaCheckCircle className="text-primary" />
                  Cours d’essai & inscription
                </div>

                <h2 className="mt-6 font-barlow text-4xl font-bold leading-tight tracking-tight text-white sm:text-5xl">
                  Envie de découvrir{" "}
                  <span className="text-primary">{clubName}</span> ?
                </h2>

                <p className="mt-5 max-w-2xl text-base leading-8 text-white/75">
                  Consultez les horaires, découvrez les modalités d’inscription
                  et contactez le club pour préparer votre venue ou celle de
                  votre enfant.
                </p>

                <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                  <Link
                    to="/inscription-google-form"
                    className="inline-flex items-center justify-center gap-3 rounded-full bg-primary px-7 py-4 text-sm font-bold text-white shadow-[0_20px_60px_rgba(242,113,49,0.35)] transition hover:-translate-y-0.5 hover:brightness-110"
                  >
                    <FaUserPlus />
                    Je souhaite m’inscrire
                    <FaArrowRight className="transition group-hover:translate-x-1" />
                  </Link>

                  <Link
                    to="/horaires-tarifs"
                    className="inline-flex items-center justify-center gap-3 rounded-full border border-white/20 bg-white/10 px-7 py-4 text-sm font-bold text-white backdrop-blur-xl transition hover:-translate-y-0.5 hover:bg-white/15"
                  >
                    <FaCalendarAlt />
                    Voir les horaires & tarifs
                  </Link>
                </div>
              </div>

              <div className="grid gap-4">
                <div className="rounded-[1.7rem] border border-white/10 bg-white/10 p-5 backdrop-blur-xl">
                  <p className="text-xs font-bold uppercase tracking-[0.18em] text-white/45">
                    Étape 1
                  </p>
                  <p className="mt-2 font-barlow text-2xl font-bold text-white">
                    Je consulte les horaires
                  </p>
                  <p className="mt-2 text-sm leading-6 text-white/65">
                    Trouvez le créneau adapté à votre âge, votre niveau ou votre
                    disponibilité.
                  </p>
                </div>

                <div className="rounded-[1.7rem] border border-white/10 bg-white/10 p-5 backdrop-blur-xl">
                  <p className="text-xs font-bold uppercase tracking-[0.18em] text-white/45">
                    Étape 2
                  </p>
                  <p className="mt-2 font-barlow text-2xl font-bold text-white">
                    Je prépare mon inscription
                  </p>
                  <p className="mt-2 text-sm leading-6 text-white/65">
                    Retrouvez les informations utiles avant de rejoindre le
                    club.
                  </p>
                </div>

                {(email || phone) && (
                  <div className="flex flex-col gap-3 rounded-[1.7rem] border border-white/10 bg-white/10 p-5 backdrop-blur-xl sm:flex-row">
                    {email && (
                      <a
                        href={`mailto:${email}`}
                        className="inline-flex flex-1 items-center justify-center gap-2 rounded-full bg-white px-4 py-3 text-sm font-bold text-slate-950 transition hover:-translate-y-0.5 hover:text-primary"
                      >
                        <FaEnvelope />
                        Email
                      </a>
                    )}

                    {phone && (
                      <a
                        href={phoneHref}
                        className="inline-flex flex-1 items-center justify-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-3 text-sm font-bold text-white transition hover:-translate-y-0.5 hover:bg-white/15"
                      >
                        <FaPhoneAlt />
                        Appeler
                      </a>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}